import type { FieldErrors, FieldPath, UseFormReturn } from 'react-hook-form';

import { CLIENT_ERROR } from '@/hooks/useClientErrors';
import { isRecord, SERVER_ERROR } from '@/routes/shared/ProviderConfig/form/errorTypes';

import type { ProviderFormValues } from '../form/formModel';

const SECRET = '(?:apiKey|clientId|clientSecret)';
const FORM_PATH_RE = new RegExp(
    `^(?:connection\\.[^.]+|secrets\\.${SECRET}|pens\\.c\\d+(?:\\.(?:connected|isActive|remove|fields\\.[^.]+|secrets\\.${SECRET}))?)$`
);

/** A path the form holds a value or a message at; `from`, `to` and the like do not qualify. */
export function isFormPath(path: string): path is FieldPath<ProviderFormValues> {
    return FORM_PATH_RE.test(path);
}

/**
 * Sets each copy key at its form path and returns the paths set, in order.
 */
export function applyFieldErrors(
    form: Pick<UseFormReturn<ProviderFormValues>, 'setError'>,
    entries: readonly { path: string; messageKey: string }[],
    type: string
): string[] {
    const applied: string[] = [];
    for (const { path, messageKey } of entries) {
        if (!isFormPath(path)) continue;
        form.setError(path, { type, message: messageKey });
        applied.push(path);
    }
    return applied;
}

/**
 * The paths of the fields whose own rule refuses their value; messages set from outside do
 * not count. Paths are dotted, as the form names its fields.
 */
export function ruleErrorPaths(errors: FieldErrors<ProviderFormValues>): string[] {
    const paths: string[] = [];
    const pending: [unknown, string][] = [[errors, '']];
    while (pending.length > 0) {
        const entry = pending.pop();
        if (entry === undefined) break;
        const [node, path] = entry;
        if (!isRecord(node)) continue;
        if (typeof node.type === 'string') {
            if (node.type !== CLIENT_ERROR && node.type !== SERVER_ERROR) paths.push(path);
            continue;
        }
        for (const [key, child] of Object.entries(node))
            pending.push([child, path === '' ? key : `${path}.${key}`]);
    }
    return paths;
}
